import { useTranslate } from '@tolgee/react';
import { Button, Input, Table, Modal, Spin, message } from 'antd';
import { useRef, memo, useState, useMemo } from 'react';
import { useMemoizedFn, useRequest, useSize } from 'ahooks';
import useModal from 'hooks/useModal';
import { EditDrawer, EditDrawerSelfProps } from './EditDrawer';
import { get, map, reduce } from 'lodash-es';
import Empty from 'components/Empty';
import { queryItemGroupList, deleteItemGroup } from './api';
import { ItemGroup } from './type';
import dayjs from 'dayjs';
import { LoadingOutlined } from '@ant-design/icons';
import SpuMore from '@knowledge/knowledges/singleManage/SpuMore';
import { listDepartmentAndRoles } from 'services/api/department/department';
import { translateDepartment } from 'views/Assistant/utils';

const Entry = memo(() => {
  const { t } = useTranslate();
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const { visible, hide, show, meta } = useModal<EditDrawerSelfProps>();
  const [keyword, setKeyword] = useState('');
  const [firstLoadNodata, setFirstLoadNodata] = useState(true);
  const firstLoad = useRef(true);
  const { data, loading, refresh, error } = useRequest(
    () =>
      queryItemGroupList({
        itemGroupName: keyword,
      }),
    {
      refreshDeps: [keyword],
      debounceWait: 300,
      onSuccess: (data) => {
        const res = data.data.itemGroup;
        if (firstLoad.current && get(res, 'length', 0) === 0) {
          setFirstLoadNodata(true);
        } else {
          setFirstLoadNodata(false);
        }
        firstLoad.current = false;
      },
      onError: () => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      },
    },
  );
  const { runAsync } = useRequest((data: ItemGroup) => deleteItemGroup(data), {
    manual: true,
  });
  const { data: data2, loading: loading2 } = useRequest(() => listDepartmentAndRoles(), {
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });
  const departmentIdMap = useMemo(() => {
    return reduce(
      get(data2, 'departmentAndRoleOfItList', []),
      (acc, each) => {
        acc[each.id] = each;
        return acc;
      },
      {} as Record<string, any>,
    );
  }, [data2]);

  const confirmDelete = useMemoizedFn((record: ItemGroup) => {
    Modal.confirm({
      title: t('确认要删除该商品组吗'),
      content: t('删除后，数据将无法恢复，并且使用该商品组的配置会删除已选商品组'),
      onOk: () => {
        return runAsync(record).then(() => {
          refresh();
        });
      },
    });
  });

  const existedNames = useMemo(() => {
    return map(get(data, 'data.itemGroup', []), 'name');
  }, [data]);

  return (
    <div className="h-full p-[16px] overflow-hidden">
      <div className="h-full flex flex-col bg-[var(--fett-bg-panel)] overflow-hidden">
        <div className="h-[56px] border-0 border-b border-solid border-[var(--fett-border-default)] px-[14px] py-[12px] flex items-center justify-between flex-shrink-0">
          <div className="pl-[10px] text-[16px] text-[var(--fett-text-primary)] font-medium">
            {t('商品组')}
          </div>
          <div>
            {visible && (
              <EditDrawer
                {...meta}
                visible={visible}
                onClose={() => hide()}
                onRefresh={() => refresh()}
                existedNames={existedNames}
              />
            )}
            {!firstLoadNodata && (
              <Button
                type="primary"
                onClick={() => {
                  show();
                }}
              >
                + {t('新增商品组')}
              </Button>
            )}
          </div>
        </div>

        {firstLoad.current === true && loading ? (
          <div className="flex-grow flex flex-col items-center justify-center">
            <Spin />
          </div>
        ) : firstLoadNodata ? (
          <div className="flex-grow flex flex-col items-center justify-center">
            <Empty
              description={
                <Button
                  onClick={() => {
                    if (error) {
                      refresh();
                    } else {
                      show();
                    }
                  }}
                  type="primary"
                >
                  {error ? t('convList.refresh') : `+ ${t('新增商品组')}`}
                </Button>
              }
            />
          </div>
        ) : (
          <div className="flex-grow flex flex-col overflow-hidden">
            <div className="flex-shrink-0 px-[14px] flex justify-end pt-[12px] pb-[18px]">
              <div className="min-w-[280px]">
                <Input.Search
                  allowClear
                  placeholder={t('输入商品组名称搜索')}
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="flex-grow px-[14px] overflow-hidden pb-[16px]" ref={ref}>
              <Table<ItemGroup>
                columns={[
                  {
                    title: t('商品组名称'),
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: t('适用部门'),
                    dataIndex: 'departmentId',
                    key: 'departmentId',
                    render: (data: number[]) => {
                      if (loading2) return <Spin />;
                      return (
                        <div>
                          {map(data, (id) => {
                            return translateDepartment(get(departmentIdMap, id, {}), t);
                          }).join(',')}
                        </div>
                      );
                    },
                  },
                  {
                    title: t('knowledge.商品'),
                    dataIndex: 'count',
                    key: 'selectedItem',
                    render: (_, record) => {
                      const { isItemLoaded, selectedItem } = record;
                      if (isItemLoaded === false) {
                        return (
                          <div className="flex items-center">
                            <LoadingOutlined className="mr-[8px]" />
                            {t('查找中')}
                          </div>
                        );
                      }
                      return selectedItem.length > 0 ? (
                        <SpuMore
                          spus={map(selectedItem, (each) => {
                            return {
                              itemInternalId: each.itemInternalId,
                              skus: each.sku,
                            };
                          })}
                        >
                          {selectedItem.length}
                        </SpuMore>
                      ) : (
                        0
                      );
                    },
                  },
                  {
                    title: t('商品更新时间'),
                    dataIndex: 'itemUpdateTimeMs',
                    key: 'itemUpdateTimeMs',
                    render: (data) => {
                      return Number(data) === 0
                        ? '-'
                        : dayjs(Number(data)).format('YYYY-MM-DD HH:mm:ss');
                    },
                  },
                  {
                    title: t('knowledge.labelTable.row.createTime'),
                    dataIndex: 'createTimestampMs',
                    key: 'createTimestampMs',
                    render: (data) => {
                      return dayjs(Number(data)).format('YYYY-MM-DD HH:mm:ss');
                    },
                  },
                  {
                    title: t('aide.操作'),
                    key: 'action',
                    render: (_, record) => {
                      return (
                        <div className="ml-[-12px]">
                          <Button
                            type="text"
                            onClick={() => {
                              show({
                                itemGroup: record,
                                isItemLoaded: record.isItemLoaded,
                              });
                            }}
                          >
                            {t('marketing.编辑')}
                          </Button>
                          <CopyButton
                            record={record}
                            show={(itemGroup) => {
                              show({
                                itemGroup,
                                isItemLoaded: false,
                                fromCopy: true,
                              });
                            }}
                          />
                          <Button
                            type="text"
                            onClick={() => {
                              confirmDelete(record);
                            }}
                          >
                            {t('marketing.删除')}
                          </Button>
                        </div>
                      );
                    },
                  },
                ]}
                loading={loading}
                dataSource={get(data, 'data.itemGroup', [])}
                scroll={{
                  y: size?.height ? size.height - 56 - 12 - 18 - 24 - 16 : 0,
                }}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => t('feedback.共x项', { x: total }),
                }}
              ></Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const CopyButton = memo((props: { record: ItemGroup; show: (record: ItemGroup) => void }) => {
  const { record, show } = props;
  const { t } = useTranslate();
  const handleCopy = useMemoizedFn(() => {
    const { itemFilter, blacklist, name, departmentId } = record;
    show({
      itemFilter,
      blacklist,
      departmentId,
      name: removeZeroWidthChars(`${name}-${t('marketing.复制')}`),
    } as any);
  });

  return (
    <Button onClick={() => handleCopy()} type="text">
      {t('marketing.复制')}
    </Button>
  );
});

function removeZeroWidthChars(str: string) {
  return str.replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
}

Entry.displayName = 'ItemGroupEntry';

export default Entry;
